/**
 * Track Zone Analysis
 *
 * Main orchestration module for analyzing track zones.
 * Coordinates zone detection, metrics calculation, and impact assessment
 * to produce actionable insights for riders.
 */

import { type Lap } from '../../domain/lap/Lap'
import {
  type TrackZone,
  type ZoneAnalysisResult,
  type ZoneDetectionConfig,
  type ZoneImpact,
} from './TrackZoneTypes'
import {
  DEFAULT_ZONE_DETECTION_CONFIG,
  DEFAULT_NOISE_TOLERANCE,
} from '../config/zoneDetection'
import {
  extractZoneData,
  detectZoneBoundaries,
  alignLapDataByDistance,
  type ZoneDataPoint,
} from './ZoneDetection'
import { calculateConsensusMetrics } from './ZoneConsensus'
import { calculateVarianceMetrics } from './ZoneVariance'
import { calculateZoneLapData } from './ZoneLapData'
import { calculateZoneImpact } from './ZoneImpact'
import { smoothedAcceleration } from './mathUtils'
import { G_CONVERSION } from './LapZoneMetrics'
import type { BrakePhaseSample } from './brakePhasePoints'
import { buildZoneConsistencyPercent } from '../sessionAnalysis/zoneDisplay/referencePointConsistency/curvePercent'
import { focusConsistentMessage } from '../sessionAnalysis/zoneDisplay/referencePointConsistency/consistencyPercent'
import { computeZoneDisplayBounds } from '../sessionAnalysis/zoneDisplay/zoneDisplayBounds'
import {
  buildRecalibratedLapPoints,
  type RecalibratedLap,
} from '../sessionAnalysis/zoneDisplay/recalibratedLapPoints'
import {
  tracesFromRecalibratedLaps,
  tracesFromRawLaps,
} from '../sessionAnalysis/zoneDisplay/referencePointConsistency/speedTraces'
import {
  computeRecalibratedZoneMetrics,
  type RecalibratedZoneLapMetrics,
} from '../sessionAnalysis/zoneDisplay/recalibratedZoneMetrics'
import { buildTrackConsistencyGradient } from '../sessionAnalysis/zoneDisplay/referencePointConsistency/trackConsistencyGradient'
import {
  SOLID_TINY_LOSS_MS,
  SOLID_MIN_ZONE_PCT,
  SOLID_MAX_TIME_LOSS_MS,
  FOCUS_MAX_ZONES,
} from '../config/focusZones'
import {
  ratingTierFor,
  ZONE_RATING_TIERS,
  ZONE_PCT_GREEN_MIN,
  type RatingTier,
} from '../config/ratingBands'

/**
 * Inert stand-in for a zone's impact while it's under construction.
 * Real impact depends on consistencyPercent, which itself depends on
 * per-zone lap data derived from the zones array — so zones are built with
 * this placeholder first, then `zone.impact` is overwritten with the real,
 * percent-aware value once percent is computed. Never read before that
 * happens (nothing between zone construction and the impact recompute
 * touches `.impact`).
 */
const PLACEHOLDER_IMPACT: ZoneImpact = Object.freeze({
  estimatedTimeLossMs: 0,
  severity: 'low',
  rank: 0,
  insight: '',
})

// ============================================================================
// Main Analysis Function
// ============================================================================

/**
 * Analyze track zones from a collection of laps.
 *
 * Detects distinct zones (braking, cornering, acceleration, straight) and
 * calculates consistency metrics to identify areas for improvement.
 *
 * @param laps - Array of laps to analyze (minimum 4 recommended)
 * @param config - Zone detection configuration
 * @returns Complete zone analysis with metrics and recommendations
 */
export function analyzeTrackZones(
  laps: Lap[],
  config: ZoneDetectionConfig = DEFAULT_ZONE_DETECTION_CONFIG,
  detectionLaps?: Lap[],
  domainLaps: Lap[] = [],
  excludedLapIds: Set<string> = new Set(),
  /** Comparison-mode session → "S2" tag, keyed by session id (owner
   *  follow-up 2026-08-21). Absent for single-session callers, so their
   *  attribution/set-aside copy stays byte-identical to before. */
  sessionLabels?: Map<string, string>
): ZoneAnalysisResult {
  // Extract telemetry data from metrics laps and pre-smooth acceleration
  // at the full-lap level. GPS-derived acceleration amplifies speed noise —
  // a 9-point median on the full lap (~400 points) works well. Per-zone
  // smoothing fails when zones are small (4-5 points) because the median
  // window has no surrounding context.
  const extractedLaps = laps
    .map((lap) => ({ lap, data: extractZoneData(lap) }))
    .filter((e) => e.data.length > 0)
  const metricsLapData = extractedLaps.map((e) => smoothLapAcceleration(e.data))
  // Identity arrays stay positionally aligned with metricsLapData — the
  // filter above drops lap and data together (outlier attribution and
  // calculateZoneLapData both index these by lap position).
  const lapIds = extractedLaps.map((e) => e.lap.id)
  const lapNumbers = extractedLaps.map((e) => e.lap.lapNumber)
  const sessionIds = extractedLaps.map((e) => e.lap.sessionId)
  // The chart's own per-GPS-point acceleration series, built once per lap and
  // sliced per zone below — it drives the brake-on/brake-off markers so they
  // sit on the trace the rider reads (see brakePhasePoints). Positionally
  // aligned with lapIds by construction (same extractedLaps order).
  const lapPointSeries = extractedLaps.map((e) =>
    buildBrakePhaseSamples(e.lap.processedTelemetryData)
  )

  // Project the caller's session → "S2" tags onto this run's lapIds, ONCE,
  // reused by every zone below — never recomputed per zone since lapIds/
  // sessionIds are already positionally aligned across the whole analysis.
  let lapSessionLabels: Map<string, string> | undefined
  if (sessionLabels && sessionLabels.size > 0) {
    lapSessionLabels = new Map()
    lapIds.forEach((lapId, i) => {
      const sessionId = sessionIds[i]
      const label = sessionId ? sessionLabels.get(sessionId) : undefined
      if (label) lapSessionLabels!.set(lapId, label)
    })
  }

  if (metricsLapData.length < 2) {
    return createEmptyResult()
  }

  // Use separate detection laps for stable zone boundaries when provided.
  // Zone boundaries come from ALL laps (consistent regardless of filtering),
  // while metrics are calculated from the filtered subset only.
  const usesSeparateDetection =
    detectionLaps != null &&
    detectionLaps !== laps &&
    detectionLaps.length >= config.minSamplesPerZone

  let detectionLapData: ZoneDataPoint[][]
  if (usesSeparateDetection) {
    detectionLapData = detectionLaps!
      .map(extractZoneData)
      .filter((d) => d.length > 0)
      .map(smoothLapAcceleration)
    if (detectionLapData.length < config.minSamplesPerZone) {
      detectionLapData = metricsLapData
    }
  } else {
    detectionLapData = metricsLapData
  }

  if (detectionLapData.length < config.minSamplesPerZone) {
    return createEmptyResult()
  }

  // Detect zone boundaries using detection laps
  const boundaries = detectZoneBoundaries(detectionLapData, config)

  // When using separate detection laps, re-extract per-zone data from metrics laps
  // so that consistency scores and lap data reflect only the analyzed laps
  let metricsPerZone: ZoneDataPoint[][][]
  if (usesSeparateDetection && detectionLapData !== metricsLapData) {
    const alignedMetrics = alignLapDataByDistance(metricsLapData)
    if (alignedMetrics.length === 0 || alignedMetrics[0].length < 2) {
      return createEmptyResult()
    }
    metricsPerZone = boundaries.map((boundary) =>
      alignedMetrics.map((lapData) =>
        lapData.filter(
          (p) =>
            p.distance >= boundary.startDistance &&
            p.distance <= boundary.endDistance
        )
      )
    )
  } else {
    metricsPerZone = boundaries.map((b) => b.dataPoints)
  }

  // Track count per zone type for naming (e.g., "Braking Zone 1", "Braking Zone 2")
  const typeCount: Record<string, number> = {}

  // Calculate metrics for each zone using metrics data. Impact (severity +
  // insight) depends on consistencyPercent, which in turn depends on
  // per-lap zone data derived from consensus below — so impact is only
  // provisional here (a placeholder that's never read before it's replaced)
  // and gets its real value once percent is computed further down.
  const zones = boundaries.map((boundary, idx) => {
    const zoneData = metricsPerZone[idx]
    const consensus = calculateConsensusMetrics(zoneData)
    const variance = calculateVarianceMetrics(
      zoneData,
      config,
      boundary.type,
      boundary.startDistance,
      DEFAULT_NOISE_TOLERANCE,
      lapNumbers
    )

    // Center point from detection data (stable position for map)
    const centerIdx = Math.floor(boundary.dataPoints[0].length / 2)
    const centerPoint = boundary.dataPoints[0][centerIdx]

    // Increment type-specific counter for naming
    typeCount[boundary.type] = (typeCount[boundary.type] ?? 0) + 1
    const typeIndex = typeCount[boundary.type]

    return createTrackZone(
      idx,
      typeIndex,
      boundary,
      consensus,
      variance,
      PLACEHOLDER_IMPACT,
      centerPoint
    )
  })

  // Pass 25/26: measure each lap's zone time AND gate values (entry/min/
  // exit speed, apex location) on the RECALIBRATED frame — the same
  // physical gates for every lap. Entry speed is measured at the
  // display-window start (cornering: turn-in, owner ruling 2026-07-18;
  // braking/acceleration: the zone start — the window is never
  // start-padded there), via the SAME computeZoneDisplayBounds derivation
  // the charts and consistency percent use, so gate, chart edge, and entry
  // marker coincide by construction. Brake point / brake release / throttle
  // point stay on the raw odometer-aligned detection frame (position
  // translation to the reference frame was implemented and dropped — spec
  // §4.2 item 4: detection runs on the shared 5m grid, whose quantization
  // error is the same order as the drift being corrected, and translation
  // made drifted-lap positions worse, not better). A throw anywhere in
  // recalibration must never kill the analysis: everything falls back
  // wholesale per zone.
  // Perf dedup (final review): computeZoneDisplayBounds was independently
  // re-derived up to 3x per zone per run (entry-gating here, inside
  // buildZoneConsistencyPercent, and again for the gradient windows below).
  // The inputs (domainLaps, excludedLapIds, zones) are fixed for the whole
  // function, so it's computed ONCE per zone and threaded to every site that
  // needs it — behavior-preserving because each site's existing computation
  // (unconditional here and at the gradient windows; domainLaps-gated inside
  // buildZoneConsistencyPercent, which still applies that gate itself) is
  // unchanged, just no longer repeated.
  const zoneDisplayBounds = new Map<
    string,
    { startDistance: number; endDistance: number }
  >(
    zones.map((zone) => [
      zone.id,
      computeZoneDisplayBounds(domainLaps, zone, excludedLapIds, zones),
    ])
  )

  let zoneMetricsByZone: Array<Map<string, RecalibratedZoneLapMetrics> | null>
  let recalibrated: RecalibratedLap[] | null = null
  try {
    recalibrated = buildRecalibratedLapPoints(extractedLaps.map((e) => e.lap))
    const gatedZones = zones.map((zone) => ({
      startDistance: zone.startDistance,
      endDistance: zone.endDistance,
      entryGate: zoneDisplayBounds.get(zone.id)!.startDistance,
    }))
    zoneMetricsByZone = computeRecalibratedZoneMetrics(
      recalibrated,
      gatedZones,
      lapIds
    )
  } catch {
    recalibrated = null
    zoneMetricsByZone = boundaries.map(() => null)
  }

  // Pass 29: the consistency statistics (curve row here; chart band and map
  // gradient client-side) bin the SAME recalibrated frame the zone chart
  // plots — raw odometer binning either manufactures phantom cross-lap
  // deviation (drift shifts a lap's curve by metres) or silently drops a
  // drifted lap into bins no other lap occupies. Converted ONCE for all
  // zones; falls back wholesale to the raw frame only when recalibration
  // itself threw above. The fallback intentionally reads
  // `extractedLaps.map((e) => e.lap)` — the SAME extraction-filtered lap
  // population `recalibrated` is built from above — rather than the
  // unfiltered `domainLaps`, so the curve %/band/gradient are deterministic
  // regardless of whether recalibration threw (a lap that
  // extractZoneData() drops, e.g. one with no spatial buckets and no GPS
  // points, must be absent from both the success and the fallback trace
  // sets).
  const consistencyTraces =
    recalibrated !== null
      ? tracesFromRecalibratedLaps(recalibrated)
      : tracesFromRawLaps(extractedLaps.map((e) => e.lap))

  // Calculate per-lap data for each zone. Depends only on consensus/boundary
  // fields already set above, not on impact — safe to compute before impact
  // is finalized.
  const zoneLapData = new Map<string, ReturnType<typeof calculateZoneLapData>>()
  boundaries.forEach((boundary, idx) => {
    const zone = zones[idx]
    const zoneData = metricsPerZone[idx]
    // The per-point series is sliced with the zone's CHART DISPLAY window, not
    // its detection boundary. A marker has to be able to reach a crossing the
    // chart actually shows, and the two windows differ: refineZoneBoundaries
    // extends a braking zone to the next corner's start so the chart draws
    // braking + turn-in as one unit. Slicing at the detection end instead cut
    // the series off while the lap was still braking, so findRelease fell into
    // its "no next defined sample" branch and reported the last heavy sample
    // rather than the crossing — measured on real data, that left 29 of 87
    // laps 3-12 m early with their crossings sitting in the 1175-1180 m gap
    // between the two windows.
    const sliceBounds = zoneDisplayBounds.get(zone.id) ?? {
      startDistance: boundary.startDistance,
      endDistance: boundary.endDistance,
    }
    const lapData = calculateZoneLapData(
      zone.id,
      zoneData,
      lapIds,
      lapNumbers,
      zone.consensus,
      // Zone-relative output stays anchored on the DETECTION start, which is
      // what consumers add back as `zone.startDistance`. A brake-on found in
      // the display window's lead-in therefore reads as a negative
      // zone-relative value, which resolves to the correct absolute distance.
      boundary.startDistance,
      sessionIds,
      zoneMetricsByZone[idx] ?? undefined,
      // Brake markers only. Variance (ZoneVariance / LapZoneMetrics)
      // intentionally stays on the bucket signal — it compares laps against
      // each other, where the coarser, uniformly-biased signal is fine.
      lapPointSeries.map((samples) =>
        sliceByDistance(
          samples,
          sliceBounds.startDistance,
          sliceBounds.endDistance
        )
      )
    )
    zoneLapData.set(zone.id, lapData)
  })

  // Attach the defensible "share within tolerance" % to every zone, THEN
  // compute the real impact from it. Percent bounds are derived via
  // computeZoneDisplayBounds (buildZoneConsistencyPercent) — the SAME
  // derivation mobile's chart display uses — so the overview chip, detail
  // pill, chart band, and this % all agree by construction. `allZones: zones`
  // is safe here even though each zone's impact is still the placeholder —
  // percent's cross-zone lookups (braking-extension, turn-in) only read
  // id/type/startDistance/endDistance, never impact.
  zones.forEach((zone, idx) => {
    zone.consistencyPercent = buildZoneConsistencyPercent({
      zone,
      lapData: zoneLapData.get(zone.id) ?? [],
      domainLaps,
      excludedLapIds,
      allZones: zones,
      // Legacy callers without domain laps keep their no-curve-row behavior
      // (the % falls back to raw zone bounds); traces would resurrect the
      // row from the metrics laps and shift their baselines.
      traces: domainLaps.length > 0 ? consistencyTraces : undefined,
      // Same gate as `traces` above: only thread the precomputed bounds when
      // domainLaps is non-empty, so the domainLaps.length === 0 branch
      // inside buildZoneConsistencyPercent (raw zone bounds, no extension)
      // stays exactly as it was for legacy callers.
      displayBounds:
        domainLaps.length > 0 ? zoneDisplayBounds.get(zone.id) : undefined,
      lapSessionLabels,
    })
    zone.impact = calculateZoneImpact(
      zone.type,
      zone.variance,
      zone.consensus,
      zone.endDistance - zone.startDistance,
      zone.consistencyPercent,
      metricsPerZone[idx],
      zoneMetricsByZone[idx]
        ? [...zoneMetricsByZone[idx]!.values()].map((m) => m.timeMs)
        : undefined
    )
  })

  // Full-track score-derived map gradient (spec 2026-08-20, Task 7): each
  // zone contributes its own clean (set-aside-excluded) lap set inside its
  // window, so the map and the curve % row agree by construction; elsewhere
  // (straights, gaps between zones) all included laps score the bin. Each
  // window is the zone's DISPLAY bounds — the identical
  // computeZoneDisplayBounds call buildZoneConsistencyPercent scores
  // setAside over (braking zones extend to the next corner's start,
  // cornering starts pull back to turn-in) — because that's the window the
  // set-aside laps were judged in; raw zone bounds would apply the wrong
  // lap set in exactly those refined stretches. Needs domainLaps for the
  // same reason consistencyPercent does — legacy callers without domain
  // laps get no gradient rather than one built off the wrong baseline.
  const trackGradient =
    domainLaps.length > 0
      ? buildTrackConsistencyGradient({
          traces: consistencyTraces,
          excludedLapIds,
          zoneWindows: zones.map((z) => {
            const displayBounds = zoneDisplayBounds.get(z.id)!
            return {
              startDistance: displayBounds.startDistance,
              endDistance: displayBounds.endDistance,
              setAsideLapIds: new Set(
                z.consistencyPercent?.setAside?.lapIds ?? []
              ),
              disrupted: z.consistencyPercent?.status === 'traffic-disrupted',
            }
          }),
        })
      : null

  // Rank zones by impact (must run after the real impact is computed above)
  rankZonesByImpact(zones)

  // Split actionable zones three ways (pass 18): solid (≥90% with <0.3s on
  // the table) keeps its rules; the non-solid rest go to focus (top
  // FOCUS_MAX_ZONES by time loss) with the overflow in improve.
  const focusZones = identifyFocusZones(zones)
  const improveZones = identifyImproveZones(zones)
  const solidZones = identifySolidZones(zones)

  // Pass 27 (C1): non-solid zones that are consistent tell the pace story —
  // focus AND the improve overflow, since both are zones the app tells the
  // rider to work on.
  applyFocusConsistentMessages([...focusZones, ...improveZones])

  // Calculate track-level statistics
  const trackStats = calculateTrackStats(zones, metricsLapData)

  return {
    zones,
    focusZones,
    improveZones,
    solidZones,
    zoneLapData,
    trackStats,
    trackGradient,
  }
}

// ============================================================================
// Focus / Solid Zone Split
// ============================================================================

/** Straights and acceleration zones are structural, not actionable — they
 * appear in neither list. */
function isActionableZone(zone: TrackZone): boolean {
  return zone.type !== 'straight' && zone.type !== 'acceleration'
}

/**
 * Solid = proven habits (zone average ≥ SOLID_MIN_ZONE_PCT) with nothing
 * meaningful left on the table (< SOLID_MAX_TIME_LOSS_MS). A high-average
 * zone still losing real time stays a focus area (the time override), and
 * an unscorable zone (no %) is never solid — no proof, no badge.
 *
 * Tiny-loss override: zones with < SOLID_TINY_LOSS_MS estimated gain are
 * solid regardless of consistency % — gains this small are noise.
 */
export function isSolidZone(zone: TrackZone): boolean {
  if (zone.impact.estimatedTimeLossMs < SOLID_TINY_LOSS_MS) return true
  const zonePct = zone.consistencyPercent?.zonePct
  return (
    zonePct != null &&
    zonePct >= SOLID_MIN_ZONE_PCT &&
    zone.impact.estimatedTimeLossMs < SOLID_MAX_TIME_LOSS_MS
  )
}

/**
 * Non-solid actionable zones ordered by global impact rank (rank 1 =
 * biggest estimated time loss; rankZonesByImpact runs before the split,
 * with array/track order breaking exact-loss ties deterministically).
 */
function rankedNonSolidZones(zones: TrackZone[]): TrackZone[] {
  return zones
    .filter(isActionableZone)
    .filter((z) => !isSolidZone(z))
    .sort((a, b) => a.impact.rank - b.impact.rank)
}

/**
 * The up-to-FOCUS_MAX_ZONES biggest time losers among non-solid actionable
 * zones, in track order (pass 18 — selection by impact, display by
 * position). Focus ∪ improve ∪ solid still covers every actionable zone.
 */
export function identifyFocusZones(zones: TrackZone[]): TrackZone[] {
  return rankedNonSolidZones(zones)
    .slice(0, FOCUS_MAX_ZONES)
    .sort((a, b) => a.startDistance - b.startDistance)
}

/**
 * Focus overflow — non-solid zones that didn't make the FOCUS_MAX_ZONES
 * cut, in track order. Non-empty only when focus is full.
 */
export function identifyImproveZones(zones: TrackZone[]): TrackZone[] {
  return rankedNonSolidZones(zones)
    .slice(FOCUS_MAX_ZONES)
    .sort((a, b) => a.startDistance - b.startDistance)
}

/**
 * Actionable zones that ARE solid — the complement of focus ∪ improve.
 * Solid zones satisfy either: tiny-loss (<100ms) OR (≥90% consistency AND <300ms time loss).
 * Exported for direct unit testing of the startDistance sort.
 */
export function identifySolidZones(zones: TrackZone[]): TrackZone[] {
  return zones
    .filter(isActionableZone)
    .filter(isSolidZone)
    .sort((a, b) => a.startDistance - b.startDistance)
}

/** Pass 27 (C1): a zone the app tells you to work on — focus or improve
 *  overflow — must never claim "nothing to fix". Membership is only known
 *  after the impact-ranked split, so the consistent-state message is
 *  finalized here, replacing BOTH bare-consistent variants (plain and
 *  one-off). Mutates in place — the same objects
 *  `composeZoneConsistencyPercent` just built. */
export function applyFocusConsistentMessages(
  actionableZones: TrackZone[]
): void {
  for (const zone of actionableZones) {
    const percent = zone.consistencyPercent
    if (percent?.status === 'ok' && percent.isConsistent) {
      percent.message = focusConsistentMessage(zone.impact.estimatedTimeLossMs)
    }
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function createEmptyResult(): ZoneAnalysisResult {
  return {
    zones: [],
    focusZones: [],
    improveZones: [],
    solidZones: [],
    zoneLapData: new Map(),
    trackStats: {
      totalZones: 0,
      highVarianceZones: 0,
      overallConsistency: 0,
      totalTimeLossMs: 0,
      trackLength: 0,
    },
    trackGradient: null,
  }
}

function createTrackZone(
  idx: number,
  typeIndex: number,
  boundary: ReturnType<typeof detectZoneBoundaries>[0],
  consensus: ReturnType<typeof calculateConsensusMetrics>,
  variance: ReturnType<typeof calculateVarianceMetrics>,
  impact: ReturnType<typeof calculateZoneImpact>,
  centerPoint?: {
    lat: number
    lng: number
    distance: number
    speed: number
    acceleration: number
    leanAngle: number
  }
): TrackZone {
  const typeName =
    boundary.type.charAt(0).toUpperCase() + boundary.type.slice(1)

  return {
    id: `zone-${idx + 1}`,
    type: boundary.type,
    startDistance: boundary.startDistance,
    endDistance: boundary.endDistance,
    name: `${typeName} Zone ${typeIndex}`,
    consensus,
    variance,
    impact,
    centerPoint: centerPoint
      ? { lat: centerPoint.lat, lng: centerPoint.lng }
      : undefined,
    consistencyPercent: null,
  }
}

function rankZonesByImpact(zones: TrackZone[]): void {
  const sorted = [...zones].sort(
    (a, b) => b.impact.estimatedTimeLossMs - a.impact.estimatedTimeLossMs
  )
  sorted.forEach((zone, idx) => {
    zone.impact.rank = idx + 1
  })
}

/** Session consistency = mean of applicable zones' composed %s, where each
 *  zone's % is the average of its metrics. Defensible as "the average of
 *  your zones"; null when no zone has a %. Badge colour comes from
 *  zonePctBand (green from 90). */
export function sessionConsistencyPercent(zones: TrackZone[]): number | null {
  const pcts = zones
    .map((z) => z.consistencyPercent?.zonePct)
    .filter((p): p is number => p != null)
  if (pcts.length === 0) return null
  return Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length)
}

export interface SessionConsistencySummary {
  /** round(mean of rated zones' zonePct) — identical to sessionConsistencyPercent. */
  pct: number
  /** Rated zones at/above ZONE_PCT_GREEN_MIN. */
  greenZones: number
  /** Zones with a non-null zonePct. */
  ratedZones: number
  /** Badge tier of the average itself (owner-ruled 2026-07-18 after device
   *  check: the badge must agree with the shown number — 91% reads
   *  Excellent). */
  tier: RatingTier
}

/** The session scorecard's headline (pass 27 C5). The number is the plain
 *  average of zone %s and the badge reads that number's own tier — no
 *  hidden demotion rules (a guard demoting green while weak zones existed
 *  shipped first, then was reversed by the owner on device: "91% should be
 *  excellent"). The zone-count line was removed per spec 2026-08-20,
 *  superseding pass-27 C5. */
export function sessionConsistencySummary(
  zones: TrackZone[]
): SessionConsistencySummary | null {
  const pct = sessionConsistencyPercent(zones)
  if (pct == null) return null
  const rated = zones.filter((z) => z.consistencyPercent?.zonePct != null)
  const greenZones = rated.filter(
    (z) => z.consistencyPercent!.zonePct! >= ZONE_PCT_GREEN_MIN
  ).length
  return {
    pct,
    greenZones,
    ratedZones: rated.length,
    tier: ratingTierFor(pct, ZONE_RATING_TIERS),
  }
}

function calculateTrackStats(
  zones: TrackZone[],
  allLapData: ReturnType<typeof extractZoneData>[]
) {
  const trackLength =
    allLapData[0]?.length > 0
      ? allLapData[0][allLapData[0].length - 1].distance
      : 0

  const highVarianceZones = zones.filter(
    (z) => z.consistencyPercent?.band === 'red'
  ).length
  const overallConsistency = sessionConsistencyPercent(zones) ?? 100
  const totalTimeLossMs = zones.reduce(
    (sum, z) => sum + z.impact.estimatedTimeLossMs,
    0
  )

  return {
    totalZones: zones.length,
    highVarianceZones,
    overallConsistency,
    totalTimeLossMs,
    trackLength,
  }
}

/**
 * Returns a copy of the lap data with acceleration smoothed via 9-point median
 * over the full lap (~400 points). Must happen before zone slicing so even tiny
 * zones (4-5 pts) benefit from surrounding lap context.
 */
function smoothLapAcceleration(lapData: ZoneDataPoint[]): ZoneDataPoint[] {
  return lapData.map((pt, i) => ({
    ...pt,
    acceleration: smoothedAcceleration(lapData, i),
  }))
}

/**
 * The per-GPS-point signal the Brake % chart plots: `acceleration[i]` is
 * index-aligned with `gpsPoints[i]` (see lapComparison/twoPhaseProcessing
 * computeBaseDataPoints). Points the physical-consistency validator flagged
 * as `speedTrendMismatch` are left undefined — that's exactly where the chart
 * draws a gap, so the marker must not be placed from them either. A lap with
 * no acceleration array yields all-undefined samples, and the brake-phase
 * derivation then declines and the bucket fallback applies.
 */
function buildBrakePhaseSamples(
  processed: Lap['processedTelemetryData']
): BrakePhaseSample[] {
  const gpsPoints = processed.gpsPoints ?? []
  const acceleration = processed.acceleration ?? []

  return gpsPoints.map((gps, i) => {
    const accel = acceleration[i]
    return {
      distance: gps.distanceMeters,
      speed: gps.speedKmh,
      accelerationG:
        accel && !accel.speedTrendMismatch
          ? accel.longitudinalMs2 / G_CONVERSION
          : undefined,
    }
  })
}

function sliceByDistance(
  samples: BrakePhaseSample[],
  startDistance: number,
  endDistance: number
): BrakePhaseSample[] {
  return samples.filter(
    (s) => s.distance >= startDistance && s.distance <= endDistance
  )
}
